import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, getPlayer, setPlayer, WHATSAPP } from "../lib/api";
import { DIAL_CODES } from "../lib/dialCodes";
import Confetti from "./Confetti";
import InstallButton from "./InstallButton";
import { toast } from "sonner";
import { Gift, X, Star, ShieldCheck, Ticket } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return ""; }
};

/**
 * Flux de gain (roue, grattage, memory, runner) : résultat → réclamation → bon QR.
 * Validité incitative : bon valable 5 jours sans compte, 10 jours avec un compte
 * (nom + prénom + mot de passe). Le compte est créé AVANT la réclamation pour figer les 10 j.
 */
export default function ClaimFlow({ result, onClose }) {
  const player = getPlayer();
  const [form, setForm] = useState({
    first_name: player?.first_name || "", last_name: "",
    dial: "+596", number: "", consent: false,
  });
  const [pwd, setPwd] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [celebrate, setCelebrate] = useState(!!(result && result.is_win));

  const closeAll = () => { setVoucher(null); setCelebrate(false); onClose(); };
  const wantsAccount = !player && pwd.length >= 4;

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error("Ton prénom est requis"); return; }
    let phone;
    if (player) {
      phone = player.phone;
    } else {
      const digits = form.number.replace(/\D/g, "");
      if (digits.length < 6) { toast.error("Numéro invalide"); return; }
      phone = `${form.dial} ${form.number.replace(/\s/g, "")}`;
    }
    setClaiming(true);
    try {
      // 1) Créer / connecter le compte AVANT la réclamation → validité 10 jours (figée).
      if (wantsAccount) {
        if (!form.last_name.trim()) { toast.error("Ton nom est requis pour créer le compte"); setClaiming(false); return; }
        try {
          const { data } = await api.post("/auth/register", {
            first_name: form.first_name, last_name: form.last_name, phone, password: pwd, consent: form.consent,
          });
          setPlayer(data.token, { phone: data.phone, first_name: data.first_name });
        } catch (e) {
          if (e?.response?.status === 409) {
            try {
              const { data } = await api.post("/auth/login", { phone, password: pwd });
              setPlayer(data.token, { phone: data.phone, first_name: data.first_name });
            } catch {
              toast.error("Ce numéro a déjà un compte : mot de passe incorrect.");
              setClaiming(false); return;
            }
          } else { toast.error(e?.response?.data?.detail || "Erreur lors de la création du compte"); setClaiming(false); return; }
        }
      }
      // 2) Réclamer le bon (le serveur applique 10 j si le numéro a un compte, sinon 5 j).
      const { data } = await api.post("/claim", {
        spin_id: result.spin_id, first_name: form.first_name, last_name: form.last_name,
        phone, consent: form.consent, origin: window.location.origin,
      });
      localStorage.setItem("yum_phone", phone);
      setVoucher({ ...data, phone });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setClaiming(false); }
  };

  const whatsappHref = voucher
    ? `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
        `Bonjour YÜM ! 🎉 J'ai gagné ${voucher.label}. Mon code : ${voucher.code}${voucher.qr_target ? " — " + voucher.qr_target : ""}`.trim())}`
    : "#";

  if (!result) return null;
  const days = voucher?.validity_days || (player ? 10 : 5);
  const daysLong = days >= 10;

  const ctaLabel = claiming ? "…"
    : player ? "Récupérer mon cadeau (10 j) 🎁"
    : wantsAccount ? "Créer mon compte & récupérer (10 j) 🎁"
    : "Récupérer mon bon (5 j)";

  return (
    <>
      {celebrate && <Confetti />}

      {/* Réclamation (gain) */}
      {!voucher && result.is_win && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 overflow-y-auto" data-testid="claim-modal">
          <div className="relative bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl yum-pop my-auto">
            <h2 className="text-2xl font-black text-gray-900 mb-1 leading-tight" data-testid="claim-prize">
              🎉 Tu as gagné {result.label} !
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              {player ? "Récupère ton cadeau en boutique." : "Laisse tes coordonnées pour récupérer ton cadeau."}
            </p>

            <input data-testid="claim-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              placeholder="Ton prénom" className="w-full border-2 border-gray-200 rounded-2xl px-4 py-3 mb-3 focus:border-[#F9B81E] outline-none" />

            {player ? (
              <div className="flex items-center justify-center gap-2 text-sm text-[#1a7a3c] bg-[#e9f9ef] rounded-xl py-2 mb-4">
                <ShieldCheck size={16} /> Compte {player.phone} — bon valable <b>10 jours</b>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <select data-testid="claim-dial" value={form.dial} onChange={(e) => setForm({ ...form, dial: e.target.value })}
                    className="border-2 border-gray-200 rounded-2xl px-2 py-3 focus:border-[#F9B81E] outline-none bg-white text-sm max-w-[110px]">
                    {DIAL_CODES.map((c, i) => (<option key={c.code + i} value={c.code}>{c.flag} {c.code}</option>))}
                  </select>
                  <input data-testid="claim-number" type="tel" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
                    placeholder="Ton numéro WhatsApp" className="flex-1 min-w-0 border-2 border-gray-200 rounded-2xl px-4 py-3 focus:border-[#F9B81E] outline-none" />
                </div>

                {/* Encart incitatif : créer un compte pour passer de 5 à 10 jours */}
                <div className={`text-left rounded-2xl p-3 mb-3 border-2 transition-colors ${wantsAccount ? "border-[#F9B81E] bg-[#FFF8E8]" : "border-gray-200 bg-gray-50"}`}>
                  <p className="text-sm font-black text-gray-800 flex items-center gap-1.5 mb-0.5">
                    <Star size={15} className="text-[#F9B81E]" fill="#F9B81E" /> Garde ton bon <span className="text-[#E20613]">10 jours</span> au lieu de 5
                  </p>
                  <p className="text-[11px] text-gray-500 mb-2">Crée ton compte : nom + mot de passe. Tu retrouves tous tes cadeaux au même endroit.</p>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    placeholder="Ton nom" className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 mb-2 text-sm focus:border-[#F9B81E] outline-none" />
                  <input data-testid="claim-create-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                    placeholder="Choisis un mot de passe (min. 4)" className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-[#F9B81E] outline-none" />
                </div>

                <label className="flex items-start gap-2 text-left text-xs text-gray-500 mb-3">
                  <input data-testid="claim-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" />
                  <span>J'accepte de recevoir les offres YÜM par WhatsApp.</span>
                </label>

                <p className={`text-xs font-bold mb-2 ${wantsAccount ? "text-[#1a7a3c]" : "text-gray-400"}`}>
                  {wantsAccount ? "✅ Ton bon sera valable 10 jours" : "Sans compte, ton bon est valable 5 jours"}
                </p>
              </>
            )}

            <button data-testid="claim-submit" onClick={submit} disabled={claiming}
              className="w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510] disabled:opacity-60 text-base">
              {ctaLabel}
            </button>
            <button onClick={closeAll} className="mt-2 text-xs text-gray-400 hover:text-gray-600">Plus tard</button>
          </div>
        </div>
      )}

      {/* Perdu */}
      {!voucher && !result.is_win && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="loss-modal">
          <div className="relative bg-white rounded-3xl p-7 max-w-sm w-full text-center shadow-2xl yum-pop">
            <h2 className="text-2xl font-black text-gray-700 mb-2">Pas de chance 😅</h2>
            <p className="text-gray-500 mb-4">{result.label}</p>
            <button onClick={closeAll} className="w-full bg-gray-200 font-bold py-3 rounded-xl">Fermer</button>
          </div>
        </div>
      )}

      {/* Bon */}
      {voucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 overflow-y-auto" data-testid="voucher-modal">
          <div className="relative bg-white rounded-3xl p-7 max-w-sm w-full text-center shadow-2xl yum-pop my-auto">
            <button onClick={closeAll} data-testid="voucher-modal-close" className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"><X size={22} /></button>
            <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-[#FFF3D6] flex items-center justify-center"><Gift size={26} color="#E20613" /></div>
            <h2 className="text-2xl font-black text-[#E20613] mb-1">Ton bon 🎟️</h2>
            <p className="text-lg font-black text-[#F26C1E] mb-3" data-testid="voucher-label">{voucher.label}</p>
            <img src={voucher.qr} alt="QR du bon" className="mx-auto w-44 h-44 rounded-xl border-4 border-[#F9B81E]" data-testid="voucher-qr" />
            <div className="bg-[#FFF3D6] border-2 border-dashed border-[#F9B81E] rounded-xl py-2 mt-3">
              <p className="text-2xl font-black tracking-widest text-[#E20613]" data-testid="voucher-code">{voucher.code}</p>
            </div>

            {/* Validité + date de fin */}
            <div className={`rounded-xl py-2 px-3 mt-3 text-sm font-bold ${daysLong ? "bg-[#e9f9ef] text-[#1a7a3c]" : "bg-[#FFF3D6] text-[#a86400]"}`} data-testid="voucher-validity">
              {daysLong ? "🎉 Valable 10 jours" : "⏳ Valable 5 jours"}
              {voucher.expires_at && <> — jusqu'au <b>{fmtDate(voucher.expires_at)}</b></>}
            </div>

            <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="share-whatsapp" className="block w-full mt-3 bg-[#25D366] text-white font-black py-3 rounded-2xl hover:brightness-95">
              Envoyer mon bon à YÜM sur WhatsApp 📲
            </a>

            {daysLong ? (
              <div className="mt-2 flex flex-col gap-2">
                <Link to="/compte" data-testid="see-my-vouchers" className="flex items-center justify-center gap-2 w-full bg-[#E20613] text-white font-black py-3 rounded-2xl hover:bg-[#c40510]">
                  <Ticket size={18} /> Voir mes bons
                </Link>
                <InstallButton kind="games" label="📲 Ajouter l'app à l'écran d'accueil"
                  className="w-full bg-[#F9B81E] text-[#1a1206] font-black py-3 rounded-2xl hover:brightness-95" />
              </div>
            ) : (
              <p className="text-[11px] text-gray-400 mt-3">💡 La prochaine fois, crée ton compte pour garder tes bons <b>10 jours</b> et les retrouver ici.</p>
            )}
            <p className="text-xs text-gray-400 mt-3">Montre ce QR code en caisse. Valable une seule fois.</p>
          </div>
        </div>
      )}
    </>
  );
}
